import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { AnsokuGame, type PlaceResult } from './core/engine';
import { cellsExtent } from './core/shapes';
import { BoardView, CELL } from './scene/board';
import { TrayView, buildPiece, type PieceGroup } from './scene/tray';
import { BoxView } from './scene/box';
import { loadSceneAssets, ensureFonts, type SceneAssets } from './scene/assets';
import { AudioManager } from './scene/audio';
import { updateTweens, tween, easeOutCubic } from './scene/anim';
import { Hud } from './ui/hud';
import { AccountUI } from './ui/account';
import {
  loadState,
  saveState,
  loadSettings,
  saveSettings,
  hasResumableGame,
} from './core/persistence';
import { api } from './net/api';
import type { Settings, SaveState } from '@shared/types';

type Screen = 'menu' | 'game' | 'gameover';

interface DragState {
  slot: number;
  ghost: PieceGroup;
  anchorX: number;
  anchorZ: number;
  valid: boolean;
  touch: boolean; // touch input lifts the piece higher (Unity: blockOfset *= 1.5 on mobile)
}

export class GameApp {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private board!: BoardView;
  private tray!: TrayView;
  private box!: BoxView;
  private hud: Hud;
  private accountUI: AccountUI;
  private audio = new AudioManager();
  private engine = new AnsokuGame();
  private assets!: SceneAssets;
  private settings: Settings = loadSettings();

  private ambient = new THREE.AmbientLight(0xffffff, 0.9);
  private key = new THREE.DirectionalLight(0xffe6c0, 1.3);
  private candle = new THREE.PointLight(0xffb060, 0.0, 22, 1.6);

  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  private screen: Screen = 'menu';
  private drag: DragState | null = null;
  private arming = false;
  private loggedIn = false;
  // Held during the line-clear ripple and the next-tray restock so a second placement can't
  // race the animation (Unity holds GameManager.stopInput across the same windows).
  private inputLocked = false;

  // Camera framing: lerp between a pulled-back "menu" view and the "game" view.
  private camT = 0;
  private menuPos = new THREE.Vector3();
  private gamePos = new THREE.Vector3();
  private menuTarget = new THREE.Vector3();
  private gameTarget = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;

    // Exact original in-game camera, resolved from AnsokuWebGame.unity (IngamePoint world pose):
    // vertical FOV 68, ~17.8 units above the board on the +Z side, looking near-straight-down (~85deg).
    this.camera = new THREE.PerspectiveCamera(68, innerWidth / innerHeight, 0.1, 100);
    this.camera.position.set(0, 17.82, 1.56);
    this.camera.lookAt(0, 0, 0);

    this.hud = new Hud(uiRoot, {
      onContinue: () => this.continueGame(),
      onNewGame: () => this.startNewGame(),
      onOpenMenu: () => this.showMenu(),
      onUndo: () => this.undo(),
      onNextTray: () => this.nextTray(),
      onArmRotate: () => this.toggleArmRotate(),
      onPieceRotate: (slot) => this.rotateSlot(slot),
      onOpenOptions: () => this.hud.showOptions(),
      onOpenHowTo: () => this.hud.showHowTo(),
      onOpenHighscores: () => this.openHighscores(),
      onOpenAccount: () => void this.accountUI.open(),
      onBackToMenu: () => (this.screen === 'game' ? this.hud.showGame() : this.showMenu()),
      onSettingsChange: (patch) => this.applySettings(patch),
    });
    this.accountUI = new AccountUI(uiRoot, {
      onChange: (loggedIn, username) => void this.onAuthChange(loggedIn, username),
      onBack: () => this.showMenu(),
    });
  }

  async init(): Promise<void> {
    await ensureFonts();
    this.assets = await loadSceneAssets();

    this.scene.add(this.ambient);
    this.key.position.set(5, 14, 7);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.camera.near = 1;
    this.key.shadow.camera.far = 40;
    const d = 9;
    Object.assign(this.key.shadow.camera, { left: -d, right: d, top: d, bottom: -d });
    this.scene.add(this.key);
    this.candle.position.set(3.6, 3.2, 6.5);
    this.scene.add(this.candle);

    // Desk/ground: the Unity background uses the Board base wood (boardBaseMap). Tile it so it reads
    // as a wood surface instead of one stretched, blurry image (the old WoodBaseTest look).
    const deskTex = this.assets.boardBaseMap?.clone() ?? this.assets.woodMap?.clone() ?? null;
    if (deskTex) {
      deskTex.wrapS = deskTex.wrapT = THREE.RepeatWrapping;
      deskTex.repeat.set(8, 8);
      deskTex.needsUpdate = true;
    }
    const floorMat = new THREE.MeshStandardMaterial({
      map: deskTex,
      color: deskTex ? 0x6f5a3e : 0x2a1d12,
      roughness: 0.92,
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), floorMat);
    floor.rotation.x = -Math.PI / 2;
    // Dropped so the box has real depth below the board surface: the drawer slides UNDER the board
    // (hidden when closed) and the perimeter walls visually fill the well.
    floor.position.y = -1.4;
    floor.receiveShadow = true;
    this.scene.add(floor);

    this.board = new BoardView(this.assets);
    this.scene.add(this.board.group);
    this.box = new BoxView(this.assets);
    this.scene.add(this.box.group);
    this.tray = new TrayView(this.assets);
    this.scene.add(this.tray.group);
    void this.loadProps();

    this.applySettings({}, true);
    this.bindInput();
    addEventListener('resize', () => this.resize());
    this.resize();

    // Try to discover a logged-in session (Phase 4 wiring; harmless offline).
    void this.checkSession();

    this.hud.setOptionValues(this.settings);
    this.showMenu();
    this.renderer.setAnimationLoop(() => this.frame());
  }

  /** Load the hanging lantern (and align the candle light to it) for atmosphere. */
  private async loadProps(): Promise<void> {
    const loader = new GLTFLoader();
    const lantern = await new Promise<THREE.Group | null>((resolve) => {
      loader.load(
        '/assets/models/lantern.glb',
        (g) => resolve(g.scene),
        undefined,
        () => resolve(null),
      );
    });
    if (!lantern) return;
    const box = new THREE.Box3().setFromObject(lantern);
    const size = new THREE.Vector3();
    box.getSize(size);
    const s = 3.4 / Math.max(size.x, size.y, size.z, 0.001);
    lantern.scale.setScalar(s);
    lantern.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.castShadow = true;
    });
    lantern.position.set(-5.2, 6.2, 0.5);
    this.scene.add(lantern);
    this.candle.position.set(-5.0, 5.2, 0.8);
  }

  // ---- session / sync (used by the web shell) ----
  private async checkSession(): Promise<void> {
    const s = await api.session();
    await this.onAuthChange(s.authenticated, s.username ?? null, false);
  }

  /** React to login/logout: update labels and reconcile the local vs cloud save.
   *  Never pushes the empty menu engine — that would clobber an existing cloud game. */
  private async onAuthChange(loggedIn: boolean, username: string | null, pushLocal = true): Promise<void> {
    this.loggedIn = loggedIn;
    this.hud.setAccountLabel(loggedIn ? 'Account' : 'Log In');
    this.hud.setWelcome(loggedIn && username ? `Welcome ${username}` : '');
    if (loggedIn) {
      const data = await api.getUserData();
      let remote: SaveState | null = null;
      if (data && data.gameData) {
        try {
          remote = JSON.parse(data.gameData) as SaveState;
        } catch {
          remote = null;
        }
      }
      const local = loadState();
      if (remote && (!local || (remote.score ?? 0) >= (local.score ?? 0))) {
        // Cloud save is at least as advanced — adopt it locally.
        saveState(remote);
      } else if (local && pushLocal) {
        // Local save is ahead (or there's no cloud save) — push the real local save, not the engine.
        void api.saveData(JSON.stringify(local), local.highScore ?? 0);
      }
    }
    this.hud.setContinueEnabled(hasResumableGame());
  }

  // ---- screens ----
  showMenu(): void {
    this.screen = 'menu';
    this.cancelDrag();
    this.hud.setNextTrayVisible(false);
    this.hud.setContinueEnabled(hasResumableGame());
    this.hud.showMenu();
    this.transitionCam(false);
    this.box.closeLid();
    this.tray.slideClosed();
  }

  startNewGame(): void {
    this.audio.unlock();
    this.inputLocked = false;
    this.engine = new AnsokuGame({ highScore: this.bestKnownHighScore() });
    this.engine.newGame();
    this.rebuildFromEngine();
    this.screen = 'game';
    this.hud.showGame();
    this.hud.setNextTrayVisible(false);
    this.hud.setUndoEnabled(false);
    this.transitionCam(true);
    this.box.openLid();
    this.tray.slideOpen();
    this.audio.play('drawer');
    this.persist();
  }

  continueGame(): void {
    this.audio.unlock();
    this.inputLocked = false;
    const state = loadState();
    if (!state) return this.startNewGame();
    this.engine = new AnsokuGame();
    this.engine.loadSave(state);
    this.rebuildFromEngine();
    this.screen = 'game';
    this.hud.showGame();
    this.hud.setUndoEnabled(false);
    this.transitionCam(true);
    this.box.openLid();
    this.tray.slideOpen();
    // A save taken with the last block already placed has an empty drawer awaiting confirmation —
    // restore the Next Tray button rather than auto-dealing (matches the in-session flow).
    this.syncTrayButtons();
    if (this.engine.gameOver) this.toGameOver();
  }

  /** Show the Next Tray button iff the drawer is empty and the game is still live. */
  private syncTrayButtons(): void {
    const empty = this.engine.tray.every((p) => p === null);
    this.hud.setNextTrayVisible(this.screen === 'game' && empty && !this.engine.gameOver);
  }

  private toGameOver(): void {
    this.screen = 'gameover';
    this.audio.play('gameover');
    this.hud.showGameOver(this.engine.score, this.engine.workingOnHighScore);
    this.persist();
  }

  private bestKnownHighScore(): number {
    const s = loadState();
    return s?.highScore ?? 0;
  }

  private async openHighscores(): Promise<void> {
    this.hud.setLocalHighscore(this.bestKnownHighScore());
    this.hud.showHighscores();
    const entries = await api.highscores(20);
    if (entries.length) this.hud.setHighscoreList(entries);
  }

  // ---- rebuild views from engine state ----
  private rebuildFromEngine(): void {
    this.board.syncFromBoard(this.engine.board);
    this.tray.setTray(this.engine.tray);
    this.updateHud();
  }

  private updateHud(): void {
    this.hud.setScore(this.engine.score);
    this.hud.setBest(
      this.engine.workingOnHighScore
        ? 'New high score!'
        : `Previous best : ${this.engine.highScore}`,
    );
    this.hud.setRotates(this.engine.rotates);
    this.hud.setRotateBar(this.engine.rotateCharge / 100);
  }

  // ---- settings ----
  private applySettings(patch: Partial<Settings>, initial = false): void {
    this.settings = { ...this.settings, ...patch };
    if (!initial) saveSettings(this.settings);
    this.audio.setLevels(
      this.settings.soundLevel,
      this.settings.musicLevel,
      this.settings.useAmbient,
      this.settings.ambientMix,
    );
    this.applyTheme();
  }

  private applyTheme(): void {
    const dark = this.settings.darkMode;
    this.scene.background = new THREE.Color(dark ? 0x0c0805 : 0x241810);
    // Original scene has fog OFF (AnsokuWebGame.unity:17 m_Fog: 0) — keep edges crisp.
    this.scene.fog = null;
    // Ambient: original day is a cool flat blue-white {0.76,0.86,1}; night a deep blue {0.106,0.298,0.549}.
    this.ambient.color.setHex(dark ? 0x1b4c8c : 0xc2dbff);
    this.ambient.intensity = dark ? 0.32 : 0.92;
    // Directional: original day is warm gold {0.906,0.761,0.553}@1.2; night cool {0.565,0.596,0.706}.
    this.key.color.setHex(dark ? 0x90989b : 0xe7c28d);
    this.key.intensity = dark ? 0.55 : 1.2;
    // Candle is OFF in daytime in the original (candleLightIntensityDay = 0); only glows at night.
    this.candle.intensity = dark ? 26 : 0;
  }

  // ---- input ----
  private bindInput(): void {
    const el = this.renderer.domElement;
    el.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    el.addEventListener('pointermove', (e) => this.onPointerMove(e));
    el.addEventListener('pointerup', (e) => this.onPointerUp(e));
    el.addEventListener('pointercancel', () => this.cancelDrag());
  }

  private setPointer(clientX: number, clientY: number): void {
    const r = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(
      ((clientX - r.left) / r.width) * 2 - 1,
      -((clientY - r.top) / r.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
  }

  private onPointerDown(e: PointerEvent): void {
    if (this.screen !== 'game') return;
    if (this.inputLocked) return;
    this.audio.unlock();
    this.setPointer(e.clientX, e.clientY);
    const slot = this.pickTraySlot();
    if (slot === null) return;
    if (this.arming) {
      this.enableRotation(slot);
      return;
    }
    this.beginDrag(slot, e.pointerType === 'touch');
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.drag) return;
    this.setPointer(e.clientX, e.clientY);
    this.updateDrag();
  }

  private onPointerUp(_e: PointerEvent): void {
    if (!this.drag) return;
    this.endDrag();
  }

  private pickTraySlot(): number | null {
    const objects = this.tray.pickMeshes().map((m) => m.object);
    if (!objects.length) return null;
    const hits = this.raycaster.intersectObjects(objects, true);
    if (!hits.length) return null;
    let o: THREE.Object3D | null = hits[0].object;
    while (o) {
      if (o.userData && o.userData.slot !== undefined) return o.userData.slot as number;
      o = o.parent;
    }
    return null;
  }

  // ---- drag & placement ----
  private beginDrag(slot: number, touch: boolean): void {
    const piece = this.engine.tray[slot];
    if (!piece) return;
    this.tray.setSlotVisible(slot, false);
    const ghost = buildPiece(piece.cells, piece.value, this.assets, false);
    this.scene.add(ghost.group);
    this.drag = { slot, ghost, anchorX: 0, anchorZ: 0, valid: false, touch };
    this.updateDrag();
  }

  private updateDrag(): void {
    if (!this.drag) return;
    const piece = this.engine.tray[this.drag.slot];
    if (!piece) return;
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.groundPlane, hit)) return;
    // Lift the piece toward the top of the screen (−Z) so it floats ABOVE the cursor/finger
    // instead of sitting under it — the original does this (GameManager.blockOfset = 1 cell,
    // ×1.5 on touch) because players can't aim a block accurately beneath their own fingertip.
    hit.z -= (this.drag.touch ? 1.5 : 1) * CELL;
    const { w, h } = cellsExtent(piece.cells);

    // The ghost glides FREELY with the cursor — it does NOT snap cell-to-cell while moving.
    // The original moves the held block continuously every frame (GameManager.Update sets
    // touchPivot.position = pos + offset); the grid snap happens only on release, when each
    // cube drops into the cell beneath it (Cube.LoadInCube). We mirror that here.
    const liftY = 1.15;
    this.drag.ghost.group.position.set(
      hit.x - ((w - 1) / 2) * CELL,
      liftY,
      hit.z - ((h - 1) / 2) * CELL,
    );
    this.drag.ghost.group.children.forEach((child, i) => {
      const cell = piece.cells[i];
      child.position.set(cell.x, 0, cell.z);
    });

    // Best-fit landing target (the cells the block would drop into right now): rounded to the
    // grid and clamped on-board. Drives the faint landing preview and the actual placement on
    // release (Cube.CheckIfCanBePlaced) — but never the moving block itself.
    const { cx, cz } = this.board.worldToCell(hit);
    let ax = Math.round(cx - (w - 1) / 2);
    let az = Math.round(cz - (h - 1) / 2);
    ax = Math.max(0, Math.min(10 - w, ax));
    az = Math.max(0, Math.min(10 - h, az));
    const valid = this.engine.canPlaceCells(piece.cells, ax, az);
    this.drag.anchorX = ax;
    this.drag.anchorZ = az;
    this.drag.valid = valid;
    this.board.showHover(piece.cells, ax, az, valid);
  }

  private endDrag(): void {
    if (!this.drag) return;
    const { slot, anchorX, anchorZ, valid } = this.drag;
    this.disposeGhost();
    this.board.hideHover();
    const d = this.drag;
    this.drag = null;
    if (!valid) {
      this.tray.setSlotVisible(slot, true);
      return;
    }
    void d;
    const result = this.engine.placePiece(slot, anchorX, anchorZ);
    if (!result) {
      this.tray.setSlotVisible(slot, true);
      return;
    }
    this.tray.rebuildSlot(slot, null);
    this.applyPlaceResult(result);
  }

  private cancelDrag(): void {
    if (!this.drag) return;
    const slot = this.drag.slot;
    this.disposeGhost();
    this.board.hideHover();
    this.drag = null;
    this.tray.setSlotVisible(slot, true);
  }

  private disposeGhost(): void {
    if (!this.drag) return;
    for (const c of this.drag.ghost.cubes) c.dispose();
    this.drag.ghost.group.removeFromParent();
  }

  private applyPlaceResult(result: PlaceResult): void {
    // Original place click is a bright wood tap, pitch random(1.25-1.5) (scene placeBlockSoundPitch).
    this.audio.play('place', { rateRange: [1.25, 1.5] });
    for (const index of result.placed) this.board.addCube(index, result.placedValue, true);
    for (const b of result.boosts) this.board.boostCube(b.index, b.newValue, b.amount);

    if (result.cleared.length) {
      // Hold input and grey the undo button for the clear ripple — the original does both
      // (GameManager.stopInput, SpotManager.cs:87 ToggleUndoButton(false)) while cubes pop.
      this.inputLocked = true;
      this.hud.setUndoEnabled(false);

      // The score climbs AS the rows clear (one pitched pop per cube, destroyDelay 0.1s apart),
      // with the multi-line bonus woven into that climb — the original pays out cube-by-cube and
      // per-row during its destroy coroutine, NOT as one lump up front. We spread the gained
      // total (base × multiplier) in proportion to each cube's value, landing exactly on newScore.
      const scoreBefore = result.newScore - result.scoreGained; // placing alone never scores
      const mult = result.scoreGained / (result.baseScore || 1); // 1.0 unless 2+ lines cleared
      this.hud.setScore(scoreBefore);

      const STAGGER = 100; // ms, = Unity destroyDelay 0.1s
      let cumBase = 0;
      result.cleared.forEach((c, i) => {
        const delay = i * STAGGER;
        const last = i === result.cleared.length - 1;
        cumBase += c.value;
        const shown = last ? result.newScore : scoreBefore + Math.round(cumBase * mult);
        this.board.removeCube(c.index, delay);
        tween(1, () => {}, {
          delay,
          onComplete: () => {
            this.audio.play('clear', { rateRange: [1.5, 2.2] });
            this.hud.setScore(shown, last);
          },
        });
      });

      // Once the last cube has popped, fire the bonus FX and resolve the turn.
      tween(1, () => {}, {
        delay: result.cleared.length * STAGGER,
        onComplete: () => {
          this.inputLocked = false;
          if (this.screen !== 'game') {
            this.persist(); // player bailed to the menu mid-ripple — still bank the placement
            return;
          }
          this.afterPlacement(result);
        },
      });
    } else {
      this.hud.setScore(result.newScore);
      this.afterPlacement(result);
    }
  }

  /** Resolve a placement once its clear ripple (if any) has finished animating. */
  private afterPlacement(result: PlaceResult): void {
    this.hud.setBest(
      this.engine.workingOnHighScore ? 'New high score!' : `Previous best : ${this.engine.highScore}`,
    );
    this.hud.setRotates(this.engine.rotates);
    this.hud.setRotateBar(this.engine.rotateCharge / 100);

    if (result.bonusScore > 0) {
      this.audio.play('bonus');
      this.hud.toast(`Multiple line bonus!<br/>+${result.bonusScore}`);
    } else if (result.newHighScore) {
      this.hud.toast('New high score!');
    }
    if (result.rotatePointsGained > 0) {
      this.audio.play('rotatePoint');
      if (result.bonusScore <= 0 && !result.newHighScore) this.hud.toast('Extra rotation point!');
    }

    if (result.trayEmpty) {
      // Last block placed: the original does NOT auto-deal. It surfaces a "Next tray" button and
      // keeps undo live (GameManager.PlaceCube → ToggleNextTrayButton(true)), so the player can
      // undo this final placement before committing to a fresh, no-longer-undoable drawer.
      this.hud.setUndoEnabled(true);
      this.hud.setNextTrayVisible(true);
    } else {
      this.hud.setUndoEnabled(true);
      if (result.gameOver) this.toGameOver();
    }
    this.persist();
  }

  /** Player confirmed the empty drawer: deal the next tray (close → refill → open ritual). */
  private nextTray(): void {
    if (this.inputLocked) return;
    if (!this.engine.tray.every((p) => p === null)) return;
    this.inputLocked = true;
    this.hud.setNextTrayVisible(false);
    this.hud.setUndoEnabled(false);
    // Restock ritual (GameManager.PlayGetNewTrayAnimation): slide closed, refill, slide open.
    this.audio.play('drawer', { rateRange: [1.2, 1.2] }); // close: higher pitch
    this.tray.slideClosed();
    tween(1, () => {}, {
      delay: 833,
      onComplete: () => {
        this.engine.dealDrawer();
        this.tray.setTray(this.engine.tray);
        this.audio.play('drawer'); // open
        this.tray.slideOpen();
        this.persist();
        tween(1, () => {}, {
          delay: 833,
          onComplete: () => {
            this.inputLocked = false;
            if (this.engine.gameOver && this.screen === 'game') this.toGameOver();
          },
        });
      },
    });
  }

  // ---- rotation ----
  private toggleArmRotate(): void {
    if (this.engine.rotates <= 0) {
      this.hud.toast('No rotation points');
      return;
    }
    this.arming = !this.arming;
    this.hud.setArming(this.arming);
  }

  private enableRotation(slot: number): void {
    if (this.engine.enableRotation(slot)) {
      this.audio.play('click');
      this.hud.setRotates(this.engine.rotates);
      this.hud.setUndoEnabled(true);
      this.persist();
    }
    this.arming = false;
    this.hud.setArming(false);
  }

  private rotateSlot(slot: number): void {
    if (this.engine.rotatePiece(slot)) {
      this.tray.rebuildSlot(slot, this.engine.tray[slot]);
      this.audio.play('click', { rateJitter: 0.1 });
      this.persist();
    }
  }

  // ---- undo ----
  private undo(): void {
    if (this.inputLocked) return;
    if (!this.engine.undo()) return;
    this.cancelDrag();
    this.arming = false;
    this.hud.setArming(false);
    // Undoing the last placement of a drawer pulls us back out of the Next Tray state.
    this.hud.setNextTrayVisible(false);
    this.rebuildFromEngine();
    this.hud.setUndoEnabled(false);
    if (this.screen === 'gameover') {
      this.screen = 'game';
      this.hud.showGame();
    }
    this.persist();
  }

  // ---- persistence + sync ----
  private persist(): void {
    const state = this.engine.toSave();
    saveState(state);
    if (this.loggedIn) {
      void api.saveData(JSON.stringify(state), this.engine.highScore);
    }
  }

  // ---- per-frame ----
  private updatePieceHandles(): void {
    if (this.screen !== 'game') {
      for (let s = 0; s < 3; s++) this.hud.setPieceHandle(s, null);
      return;
    }
    for (let s = 0; s < 3; s++) {
      const piece = this.engine.tray[s];
      const center = this.tray.pieceWorldCenter(s);
      if (!piece || !piece.rotationEnabled || !center || (this.drag && this.drag.slot === s)) {
        this.hud.setPieceHandle(s, null);
        continue;
      }
      const p = center.clone().add(new THREE.Vector3(1.1, 0.4, -0.4));
      p.project(this.camera);
      const r = this.renderer.domElement.getBoundingClientRect();
      this.hud.setPieceHandle(s, {
        x: r.left + ((p.x + 1) / 2) * r.width,
        y: r.top + ((1 - p.y) / 2) * r.height,
      });
    }
  }

  private frame(): void {
    updateTweens(performance.now());
    this.updatePieceHandles();
    // gentle candle flicker in dark mode
    if (this.settings.darkMode) {
      this.candle.intensity = 24 + Math.sin(performance.now() * 0.006) * 3 + Math.random() * 1.5;
    }
    this.renderer.render(this.scene, this.camera);
  }

  /** Dev-only inspection hook (exposed on window in dev builds). */
  debugSnapshot(): { filled: number; score: number; tray: number; rotates: number; over: boolean } {
    return {
      filled: this.engine.board.filter((v) => v > 0).length,
      score: this.engine.score,
      tray: this.engine.tray.filter((p) => p !== null).length,
      rotates: this.engine.rotates,
      over: this.engine.gameOver,
    };
  }

  /** Dev-only: pre-fill row 0 and place a single cube to force a line clear through the full UI path. */
  debugForceClear(): void {
    for (let x = 0; x < 9; x++) this.engine.board[AnsokuGame.idx(x, 0)] = 1;
    this.board.syncFromBoard(this.engine.board);
    const single = Math.max(0, this.engine.shapes.findIndex((s) => s.cells.length === 1));
    this.engine.tray[0] = this.engine.makePiece(single, 0, false);
    this.tray.rebuildSlot(0, this.engine.tray[0]);
    const result = this.engine.placePiece(0, 9, 0);
    if (result) this.applyPlaceResult(result);
  }

  /** Dev-only: seed a small in-progress game and persist it (for save-sync tests). */
  debugSeed(score: number): void {
    this.startNewGame();
    this.engine.board[AnsokuGame.idx(2, 2)] = 3;
    this.engine.board[AnsokuGame.idx(3, 2)] = 5;
    this.engine.score = score;
    this.board.syncFromBoard(this.engine.board);
    this.persist();
  }

  /** Dev-only: fill the board so the next deal ends the game. */
  debugForceGameOver(): void {
    this.engine.board.fill(1);
    this.engine.rotates = 0;
    this.board.syncFromBoard(this.engine.board);
    this.engine.tray = [this.engine.makePiece(0, 0, false), null, null];
    if (!this.engine.hasAnyValidMove()) {
      this.engine.gameOver = true;
      this.toGameOver();
    }
  }

  private computeFramings(): void {
    const aspect = innerWidth / innerHeight;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    // Exact original poses (resolved from the Unity scene), with an aspect-based pull-back `f`
    // so the board still fits on narrow/portrait viewports. In-game = near-top-down; menu = far back.
    const f = Math.max(1, Math.min(1.7, 0.66 / aspect));
    this.gamePos.set(0, 17.82 * f, 1.56 * f);
    this.gameTarget.set(0, 0, 0);
    this.menuPos.set(0.05, 18.13 * f, 30.04 * f);
    this.menuTarget.set(0.05, 0, 2.86);
  }

  private applyCam(t: number): void {
    this.camT = t;
    this.camera.position.lerpVectors(this.menuPos, this.gamePos, t);
    this.camera.lookAt(new THREE.Vector3().lerpVectors(this.menuTarget, this.gameTarget, t));
  }

  private transitionCam(toGame: boolean): void {
    const from = this.camT;
    const to = toGame ? 1 : 0;
    tween(750, (t) => this.applyCam(from + (to - from) * t), { ease: easeOutCubic });
  }

  private resize(): void {
    this.computeFramings();
    this.applyCam(this.camT);
    this.renderer.setSize(innerWidth, innerHeight);
  }
}
